// utils/exerciseUtils.js
// Fonctions utilitaires partagées entre les exercices (Conjugaison, Vocabulaire, etc.)

// ========== OPTIONS DE LANGUE ==========
// Options de filtre de langue utilisées dans tous les exercices
// Prend la fonction `t` (react-i18next) pour renvoyer des libellés traduits selon la langue d'interface active
export const getLanguageOptions = (t) => [
  { value: 'all', label: t('langues.toutes'), flag: '🌍' },
  { value: 'fr', label: t('langues.fr'), flag: '🇫🇷' },
  { value: 'en', label: t('langues.en'), flag: '🇬🇧' },
  { value: 'es', label: t('langues.es'), flag: '🇪🇸' },
];

// ========== FONCTION : Obtenir le drapeau d'une langue ==========
/**
 * Retourne l'emoji drapeau correspondant à un code langue
 * @param {string} langue - Code langue (fr, en, es, all)
 * @returns {string} Emoji drapeau
 */
export const getLangueFlag = (langue) => {
  switch (langue) {
    case 'fr':
      return '🇫🇷';
    case 'en':
      return '🇬🇧';
    case 'es':
      return '🇪🇸';
    case 'all':
      return '🌍';
    default:
      return '🌍';
  }
};

// ========== FONCTION : Obtenir les caractères spéciaux d'une langue ==========
/**
 * Retourne la liste des caractères spéciaux (accents) pour une langue donnée
 * Utilisé pour le clavier virtuel
 * @param {string} langue - Code langue (fr, en, es)
 * @returns {string[]} Liste des caractères spéciaux
 */
export const getSpecialChars = (langue) => {
  switch (langue) {
    case 'fr':
      return ['à', 'â', 'é', 'è', 'ê', 'ë', 'î', 'ï', 'ô', 'ù', 'û', 'ü', 'ç', 'æ', 'œ'];
    case 'es':
      return ['á', 'é', 'í', 'ó', 'ú', 'ñ', 'ü', '¿', '¡'];
    case 'en':
      return []; // Pas de caractères spéciaux courants en anglais
    default:
      return ['é', 'è', 'ê', 'à', 'ù', 'ç', 'á', 'í', 'ó', 'ú', 'ñ'];
  }
};

// ========== FONCTION : Vérifier si une langue nécessite un clavier virtuel ==========
/**
 * Indique si une langue a des caractères spéciaux nécessitant un clavier virtuel
 * @param {string} langue - Code langue (fr, en, es)
 * @returns {boolean} true si la langue a des caractères spéciaux
 */
export const hasSpecialChars = (langue) => {
  return langue === 'fr' || langue === 'es';
};

// ========== FONCTION : Article défini avec élision ==========
/**
 * Lettres déclenchant l'élision en français (le/la → l')
 * Note : le 'h' n'est PAS inclus (h aspiré vs h muet trop complexe à distinguer sans dictionnaire)
 * Note : le 'y' n'est PAS inclus (on dit "le yacht", pas "l'yacht")
 */
const VOYELLES_ELISION = 'aeiouàâäéèêëîïôùûüæœ';

/**
 * Obtient l'article défini selon la langue, le genre et le nombre
 * Gère automatiquement l'élision française (le/la → l' devant voyelle)
 * @param {string} langue - Code langue (fr, es, en)
 * @param {string} genre - Genre du mot (masculin, féminin, neutre)
 * @param {string} nombre - Nombre du mot (singulier, pluriel)
 * @param {string} [mot=''] - Le mot (utilisé pour l'élision française)
 * @returns {string} Article défini
 */
export const getArticleDefini = (langue, genre, nombre, mot = '') => {
  const articles = {
    fr: {
      masculin: { singulier: 'le', pluriel: 'les' },
      féminin: { singulier: 'la', pluriel: 'les' },
    },
    es: {
      masculin: { singulier: 'el', pluriel: 'los' },
      féminin: { singulier: 'la', pluriel: 'las' },
    },
    en: {
      masculin: { singulier: 'the', pluriel: 'the' },
      féminin: { singulier: 'the', pluriel: 'the' },
      neutre: { singulier: 'the', pluriel: 'the' },
    },
  };

  const langueKey = langue || 'fr';
  const genreKey = genre || 'masculin';
  const nombreKey = nombre || 'singulier';

  let article = articles[langueKey]?.[genreKey]?.[nombreKey] || '';

  // Élision française : le/la → l' devant voyelle ou h
  if (langueKey === 'fr' && nombreKey === 'singulier' && mot) {
    const premiereLettre = mot.charAt(0).toLowerCase();
    if (VOYELLES_ELISION.includes(premiereLettre)) {
      if (article === 'le' || article === 'la') {
        article = "l'";
      }
    }
  }

  return article;
};

// ========== FACTORY : Créer une fonction insertSpecialChar ==========
/**
 * Crée une fonction pour insérer un caractère spécial dans un input
 * Version pour un seul input (ConjugationExercise, VocabularyExercise)
 * 
 * @param {React.RefObject} inputRef - Référence vers l'input
 * @param {string} currentValue - Valeur actuelle de l'input
 * @param {Function} setValue - Fonction pour mettre à jour la valeur (setState)
 * @returns {Function} Fonction insertSpecialChar(char)
 */
export const createInsertSpecialChar = (inputRef, currentValue, setValue) => {
  return (char) => {
    const input = inputRef.current;
    
    if (input) {
      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? currentValue.length;

      // Insérer le caractère à la position du curseur
      const newValue = currentValue.substring(0, start) + char + currentValue.substring(end);

      // Mettre à jour le state React
      setValue(newValue);

      // Replacer le curseur après le caractère inséré
      setTimeout(() => {
        input.focus();
        input.selectionStart = input.selectionEnd = start + 1;
      }, 0);
    }
  };
};

// ========== FACTORY : Créer une fonction insertSpecialChar pour inputs multiples ==========
/**
 * Crée une fonction pour insérer un caractère spécial dans un des inputs d'un tableau
 * Version pour plusieurs inputs (ConjugationCompleteExercise)
 * 
 * @param {React.RefObject} lastFocusedInputRef - Référence vers le dernier input focusé
 * @param {string[]} currentValues - Tableau des valeurs actuelles
 * @param {Function} setValues - Fonction pour mettre à jour les valeurs (setState)
 * @returns {Function} Fonction insertSpecialChar(char)
 */
export const createInsertSpecialCharMultiple = (lastFocusedInputRef, currentValues, setValues) => {
  return (char) => {
    const targetInput = lastFocusedInputRef.current;
    
    if (targetInput && targetInput.classList.contains('answer-input')) {
      const index = parseInt(targetInput.dataset.index);
      
      if (!isNaN(index) && index >= 0 && index < currentValues.length) {
        const start = targetInput.selectionStart ?? currentValues[index].length;
        const end = targetInput.selectionEnd ?? currentValues[index].length;

        // Construire la nouvelle valeur avec le caractère inséré
        const newValue = currentValues[index].substring(0, start) + char + currentValues[index].substring(end);

        // Mettre à jour le state
        const newAnswers = [...currentValues];
        newAnswers[index] = newValue;
        setValues(newAnswers);

        // Replacer le focus et le curseur après l'insertion
        setTimeout(() => {
          targetInput.focus();
          const newCursorPos = start + char.length;
          targetInput.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }
  };
};
